import Component from '@glimmer/component';
import { concat } from '@ember/helper';
import Heading from 'tio-reimagined/components/heading';
import Text from 'tio-reimagined/components/text';
import style from 'ember-style-modifier/modifiers/style';
import { PhRocketLaunch, PhTrendUp, PhSparkle, PhConfetti } from 'ember-phosphor-icons';

/**
 * DebtJourneyHero - Motivational hero banner showing debt freedom progress
 *
 * Displays a progress bar toward debt freedom with encouraging messaging
 * and key milestone indicators. Designed to make debt repayment feel
 * achievable and celebratory.
 *
 * @example
 * ```gts
 * <DebtJourneyHero
 *   @currentBalance={{45230}}
 *   @originalBalance={{68500}}
 *   @estimatedPayoffDate="2029-08-15"
 *   @yearProgressAmount={{2340}}
 * />
 * ```
 */
interface DebtJourneyHeroSignature {
  Args: {
    /** Current total loan balance */
    currentBalance: number;
    /** Original total loan balance (for progress calculation) */
    originalBalance: number;
    /** Estimated debt-free date */
    estimatedPayoffDate: string;
    /** Amount paid down this year */
    yearProgressAmount: number;
    /** Optional: percentage improvement vs last year */
    yearOverYearImprovement?: number;
  };
  Element: HTMLDivElement;
}

export default class DebtJourneyHero extends Component<DebtJourneyHeroSignature> {
  get progressPercent() {
    const paid = this.args.originalBalance - this.args.currentBalance;
    return Math.round((paid / this.args.originalBalance) * 100);
  }

  get progressWidth() {
    return `${this.progressPercent}%`;
  }

  get formattedBalance() {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(this.args.currentBalance);
  }

  get formattedYearProgress() {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(this.args.yearProgressAmount);
  }

  get formattedPayoffDate() {
    return new Intl.DateTimeFormat('en-US', {
      month: 'long',
      year: 'numeric',
    }).format(new Date(this.args.estimatedPayoffDate));
  }

  get motivationalMessage() {
    if (this.progressPercent >= 75) {
      return "You're in the home stretch! Debt freedom is within reach.";
    }
    if (this.progressPercent >= 50) {
      return 'Halfway there! Your dedication is paying off.';
    }
    if (this.progressPercent >= 25) {
      return 'Great momentum! Every payment brings you closer.';
    }
    return 'Your debt freedom journey has begun. Keep going!';
  }

  get iconComponent() {
    if (this.progressPercent >= 75) {
      return PhConfetti;
    }
    if (this.progressPercent >= 50) {
      return PhSparkle;
    }
    return PhRocketLaunch;
  }

  <template>
    {{! template-lint-disable no-bare-strings }}
    <div
      class="bg-hero-gradient rounded-2xl p-6 text-white shadow-lg overflow-hidden relative"
      ...attributes
    >
      {{! Background decoration - subtle floating circles }}
      <div class="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-white/6 hero-float"></div>
      <div
        class="absolute top-8 right-16 w-10 h-10 rounded-full bg-white/4 hero-float"
        {{style animation-delay="-18s"}}
      ></div>
      <div
        class="absolute -top-2 right-32 w-16 h-16 rounded-full bg-white/5 hero-float"
        {{style animation-delay="-35s"}}
      ></div>

      {{! Header }}
      <div class="flex items-start justify-between mb-4 relative">
        <div class="flex items-center gap-3">
          {{#let this.iconComponent as |Icon|}}
            <div class="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
              <Icon @size={{28}} weight="duotone" />
            </div>
          {{/let}}
          <div>
            <Heading @level={{1}} class="text-white text-2xl font-bold">
              Your Debt Freedom Journey
            </Heading>
            <Text class="text-white/80">{{this.motivationalMessage}}</Text>
          </div>
        </div>
        {{#if @yearOverYearImprovement}}
          <div
            class="flex items-center gap-1 px-3 py-1.5 bg-emerald-400/20 rounded-full text-emerald-200 text-sm font-medium"
          >
            <PhTrendUp @size={{16}} />
            <span>{{@yearOverYearImprovement}}% faster than last year</span>
          </div>
        {{/if}}
      </div>

      {{! Progress Section }}
      <div class="relative mb-4">
        {{! Progress bar background }}
        <div class="h-4 bg-white/20 rounded-full overflow-hidden backdrop-blur-sm">
          {{! Progress bar fill }}
          <div
            class="h-full rounded-full transition-all duration-1000 ease-out"
            {{style
              width=(concat this.progressPercent "%")
              background-color="var(--color-ocean-400)"
            }}
          ></div>
        </div>

        {{! Progress labels }}
        <div class="flex justify-between mt-2 text-sm">
          <span class="text-white/70">{{this.formattedBalance}} remaining</span>
          <span class="font-semibold">{{this.progressPercent}}% paid off</span>
          <span class="text-white/70">$0 by {{this.formattedPayoffDate}}</span>
        </div>
      </div>

      {{! Stats Row }}
      <div class="grid grid-cols-3 gap-4 pt-4 border-t border-white/20">
        <div class="text-center">
          <div class="text-2xl font-bold">{{this.formattedYearProgress}}</div>
          <Text class="text-white/70 text-sm">Paid this year</Text>
        </div>
        <div class="text-center border-l border-r border-white/20">
          <div class="text-2xl font-bold">{{this.formattedPayoffDate}}</div>
          <Text class="text-white/70 text-sm">Debt-free date</Text>
        </div>
        <div class="text-center">
          <div class="text-2xl font-bold">{{this.progressPercent}}%</div>
          <Text class="text-white/70 text-sm">Progress</Text>
        </div>
      </div>
    </div>
  </template>
}
